import router from 'next/router'
import { useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { MacrosEditor } from '../../../assets/macro/Editor'
import { ReferencesEditor } from '../../../assets/reference/Editor'
import { ArticleDraft, deleteDoc } from '../../../firebase'
import { useAuthState } from '../../../firebase/hooks'
import { useElementSize, useMobileKeyboardStatus } from '../../../hooks'
import { useArticleDraftForm } from '../../../hooks/forms/useArticleDraftForm'
import { Designed } from '../../../types'
import {
  EditorTypeChangeNav,
  EditorTypeName,
} from '../../editor_addons/editor_type_changer'
import { MathdownEditor } from '../../mathdown/editor'
import { useMathdownEditorWindowMode } from '../../mathdown/editor/hooks/useWindowMode'
import { usePopupMessage } from '../../messenger'
import { SpinnerButton } from '../../spinners'
import { CollaborativeSetting } from '../CollaborativeSetting'
import { MobileHeader } from './MobileHeader'

type Props = {
  draft: ArticleDraft
}

export const ArticleDraftForm = ({
  draft,
  ...containerProps
}: Designed<Props>) => {
  const { user } = useAuthState()
  const { setPopupMessage } = usePopupMessage()

  const keyboardStatus = useMobileKeyboardStatus()
  const { elementRef: headerRef, height: headerHeight } = useElementSize()

  const [editorType, setEditorType] = useState<EditorTypeName>('mathdown')
  const { windowMode, setWindowMode } = useMathdownEditorWindowMode()

  const {
    macros,
    references,
    errors,
    isValid,
    body,
    handleBodyChange,
    title,
    handleTitleChange,
    saveTitleChange,
    sharedPath,
    limitationTypeField,
    editableUidsFieldArray,
    saveEditabilityChange,
    handleSave,
    room,
    saveBodyChange,
  } = useArticleDraftForm(draft)

  const [isDeleting, setIsDeleting] = useState(false)
  const [isPreparing, setIsPreparing] = useState(false)
  const isSubmitting = isDeleting || isPreparing

  return (
    <Form {...containerProps} onSubmit={(e) => e.preventDefault()}>
      <div className="d-flex flex-column h-100">
        <MobileHeader
          draft={draft}
          headerRef={headerRef}
          headerHeight={headerHeight}
          title={title}
          onChangeTitle={handleTitleChange}
          saveTitleChange={saveTitleChange}
          editorType={editorType}
          setEditorType={setEditorType}
          sharedPath={sharedPath}
          saveEditabilityChange={saveEditabilityChange}
          editableUidsFieldArray={editableUidsFieldArray}
          limitationTypeField={limitationTypeField}
          errors={errors}
          onSave={handleSave}
          windowMode={windowMode}
          setWindowMode={setWindowMode}
        />
        <Form.Control
          onChange={handleTitleChange}
          value={title}
          onBlur={saveTitleChange}
          isInvalid={!!errors.title}
          placeholder="タイトルを入力してください"
          className="mb-2 d-none d-md-block"
        />
        <EditorTypeChangeNav
          activeEditorType={editorType}
          onChange={setEditorType}
          className="mb-2 d-none d-md-flex"
        />
        <div className="flex-grow-1 overflow-x-hidden overflow-y-auto">
          <MathdownEditor
            className={
              editorType !== 'mathdown' ? 'd-none' : 'h-100 w-100 px-2 p-md-0'
            }
            design="colorful"
            defaultValue={body}
            onSave={saveBodyChange}
            mobileMenuPosition={{
              top: keyboardStatus === 'close' ? headerHeight : 0,
              right: 8,
              left: 8,
            }}
            onChange={handleBodyChange}
            macros={macros ?? []}
            windowMode={windowMode}
            onChangeWindowMode={setWindowMode}
            references={references ?? []}
            room={room}
          />
          <ReferencesEditor
            parentRef={draft.ref}
            references={references ?? []}
            className={
              editorType !== 'references' ? 'd-none' : 'h-100 w-100 p-2 p-md-0'
            }
          />
          <MacrosEditor
            parentRef={draft.ref}
            macros={macros ?? []}
            className={
              editorType !== 'macros' ? 'd-none' : 'h-100 w-100 p-2 p-md-0'
            }
            windowMode={windowMode}
          />
        </div>
        {user && (
          <div className="d-none d-md-flex justify-content-between flex-row-reverse gap-5 mt-2">
            <div className="d-flex gap-4 flex-shrink-0">
              {user.uid === draft.created_by && (
                <SpinnerButton
                  type="button"
                  size={'sm'}
                  loading={isDeleting}
                  loadingText="削除中..."
                  variant="danger"
                  disabled={isSubmitting}
                  onClick={async () => {
                    try {
                      const windowConfirm =
                        window.confirm('本当に削除しますか？')
                      if (!windowConfirm) return
                      setIsDeleting(true)
                      await deleteDoc(draft.ref)
                      await router.push(`/${draft.parent_id}`)
                      setPopupMessage('下書きを削除しました。', 'danger')
                    } catch (error) {
                      if (error instanceof Error)
                        setPopupMessage(error.message, 'danger')
                      setIsDeleting(false)
                    }
                  }}
                >
                  下書きを削除
                </SpinnerButton>
              )}
              <Button
                type="button"
                variant="secondary"
                size={'sm'}
                onClick={async () => {
                  await router.push(`/article_drafts`)
                }}
                disabled={isSubmitting}
              >
                下書き一覧へ
              </Button>
              {user.uid === draft.created_by && (
                <SpinnerButton
                  type="button"
                  size={'sm'}
                  loading={isPreparing}
                  loadingText="準備中..."
                  disabled={isSubmitting || !isValid}
                  onClick={async () => {
                    try {
                      setIsPreparing(true)
                      await handleSave()
                      await router.push(
                        `/article_drafts/${draft.id}/publication`
                      )
                    } catch (error) {
                      if (error instanceof Error)
                        setPopupMessage(error.message, 'danger')
                      setIsPreparing(false)
                    }
                  }}
                >
                  投稿に進む
                </SpinnerButton>
              )}
            </div>
            {user.uid === draft.created_by && (
              <CollaborativeSetting
                as="button"
                saveEditability={saveEditabilityChange}
                sharedPath={sharedPath}
                editableUidsFieldArray={editableUidsFieldArray}
                limitationTypeField={limitationTypeField}
                size="sm"
              />
            )}
          </div>
        )}
      </div>
    </Form>
  )
}
